import Axios from "axios";
import { createApi } from "@synvox/api";
import { API_HOST, INTEGRATIONS_HOST } from "../env";
import parseQuery from "../util/parseQuery";
import ms from "ms";
import { caseMethods, transformKey, transformKeys } from "./case";
import queryString from "qs";

export type ApiResponseData<TData, TMeta = unknown> = {
  data: TData;
  meta?: TMeta;
  errors?: Record<string, string[]>;
};

export type DataWithErrors<T> = T & {
  errors?: Partial<Record<keyof T, string[]> & { base: string }>;
};

export type CursorPaginationData = {
  hasMore: boolean;
};

/** Generates a snake-case query string compatible with Skipio and Stargate APIs. */
export function generateQueryString(queryParams?: Record<string, unknown>) {
  if (!queryParams || !Object.keys(queryParams).length) return "";

  return queryString.stringify(transformKeys(queryParams, caseMethods.snake), {
    encodeValuesOnly: true,
    arrayFormat: "brackets",
  });
}

const axios = Axios.create({
  baseURL: API_HOST + "/api/v2",
  withCredentials: true,
});

let asUserId = parseQuery().asUserId || undefined;
export function isImpersonating() {
  return Boolean(asUserId);
}

axios.interceptors.request.use(function (config) {
  if (asUserId) {
    if (config.url!.includes("?")) config.url += "&";
    else config.url += "?";
    config.url += `__as=${asUserId}`;
  }

  config.headers = {
    ...config.headers,
    "Skipio-API-Token":
      window.localStorage.token ||
      window.sessionStorage.token ||
      process.env.REACT_APP_API_TOKEN ||
      "",
    "X-Skipio-Client": "split v1",
  };
  return config;
});

/**
 * This will eventually replace the existing axios instance for use with react
 * query and typed api request functions (api/*.ts).
 * The addition of the response interceptor that changes the structure of the
 * response prevents this from being a drop-in replacement.
 */
export const skipioAxios = Axios.create({
  baseURL: API_HOST + "/api/v2",
  withCredentials: true,
  paramsSerializer: (params) => generateQueryString(params),
  transformRequest: (data) =>
    JSON.stringify(transformKeys(data, caseMethods.snake)),
  transformResponse: (res) => transformKeys(JSON.parse(res), caseMethods.camel),
});

skipioAxios.interceptors.request.use(function (config) {
  if (asUserId) {
    if (config.url!.includes("?")) config.url += "&";
    else config.url += "?";
    config.url += `__as=${asUserId}`;
  }
  return config;
});

skipioAxios.interceptors.request.use(function (config) {
  config.headers = {
    ...config.headers,
    "Content-Type": "application/json",
    "Skipio-API-Token":
      window.localStorage.token ||
      window.sessionStorage.token ||
      process.env.REACT_APP_API_TOKEN ||
      "",
    "X-Skipio-Client": "split v1",
  };
  return config;
});

// Make sure the request url is in snake case
skipioAxios.interceptors.request.use(function (config) {
  if (config.url) {
    config.url = transformKey(config.url, caseMethods.snake);
  }
  return config;
});

let stargateToken: null | string = null;
let stargateTokenPromise: null | Promise<void> = null;

export async function refreshStargateToken() {
  setTimeout(refreshStargateToken, ms("30m"));
  const res = await skipioApi.stargateToken.get();
  const {
    data: { token },
  } = res;
  stargateToken = token;
  resetStargateApiCache();
}

export function useStargateToken() {
  // use the closest suspense boundary
  if (!stargateToken) {
    if (stargateTokenPromise) throw stargateTokenPromise;

    const promise = refreshStargateToken().catch(() => {
      stargateTokenPromise = null;
    });

    stargateTokenPromise = promise;
    throw promise;
  }

  return stargateToken;
}

export const stargateAxios = Axios.create({
  baseURL: `${INTEGRATIONS_HOST}/api/v1`,
});

stargateAxios.interceptors.request.use(function (config) {
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${stargateToken}`,
  };
  return config;
});

const {
  api: skipioApi,
  useApi: useSkipioApi,
  touch: touchSkipioApi,
  reset: resetSkipioApiCache,
  preload,
} = createApi<any>(axios, {
  requestCase: "snake",
  responseCase: "camel",
  retryCount: 4,
});

const { useApi: useSkipioApiNonTransformed } = createApi<any>(axios, {
  responseCase: "camel",
  retryCount: 4,
});

const {
  api: stargateApi,
  useApi: useStargateApiReal,
  touch: touchStargateApi,
  reset: resetStargateApiCache,
  preload: preloadStargate,
} = createApi<any>(stargateAxios, {
  requestCase: "snake",
  responseCase: "camel",
});

function useStargateApi() {
  useStargateToken();
  return useStargateApiReal();
}

export {
  skipioApi,
  useSkipioApi,
  useSkipioApiNonTransformed,
  touchSkipioApi,
  resetSkipioApiCache,
  preload,
  stargateApi,
  useStargateApi,
  touchStargateApi,
  resetStargateApiCache,
  preloadStargate,
};
